import { useThemeStore } from "@stores/theme.store";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { IoCopy, IoChevronDown, IoChevronUp } from "react-icons/io5";
import copy from "copy-to-clipboard";
import moment from "moment";
import { Button, message } from "antd";
import { toRp } from "@utils/helper";
import { PopupConfirm } from "@components/general/popup";
import { NoticeType } from "antd/es/message/interface";
import { useMutation } from "@tanstack/react-query";
import { FinanceService } from "@services/finance.service";
import { DetailRequestTagihanVA } from "@services/dtos/finance.dto";
export const VaDetailPage = () => {
  const { state } = useLocation();
  const { setHeader, setAppBar, setBottomNavigation } = useThemeStore(
    (state) => state,
  );
  const [messageApi, contextHolder] = message.useMessage();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");
  const [trxID, setTrxID] = useState<string>("");
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [popupTextConfirm, setPopupTextConfirm] = useState<string>("");
  const [popupCancel, setPopupCancel] = useState<boolean>(false);
  const [status, setStatus] = useState<string>(state?.vaStatus);
  const [openDetails, setOpenDetails] = useState<number[]>([]);

  const { mutate, isPending } = useMutation({
    mutationFn: FinanceService.virtualAccountCancel,
    onSuccess: (response) => {
      if (response.success) {
        setStatus("EXPIRED");
        setTrxID("");
        setShowMessage(true);
        setShowMessageText("Virtual account berhasil dibatalkan!");
        setMessageType("success");
      } else {
        setShowMessage(true);
        setShowMessageText(response.message);
        setMessageType("error");
      }
    },
    onError: (error: any) => {
      setShowMessage(true);
      setShowMessageText(error.message ?? "Internal Error");
      setMessageType("error");
    },
  });

  const handleConfirm = () => {
    setPopupCancel(false);
    mutate(trxID);
  };

  const onCopy = () => {
    copy(state?.vaNumber);
    setShowMessage(true);
    setShowMessageText("Nomor VA berhasil disalin!");
    setMessageType("success");
  };

  const onCopyNominal = () => {
    copy(
      state.vaBankName.toLowerCase() === "bni"
        ? state?.vaTrxAmount + state?.vaBankFee
        : state?.vaTrxAmount,
    );
    setShowMessage(true);
    setShowMessageText("Nominal Pembayaran berhasil disalin!");
    setMessageType("success");
  };

  const onOpenPopupCancel = (id: string) => {
    setTrxID(id);
    setPopupTitle("Batalkan Pembayaran");
    setPopupMessage(
      "Jika dibatalkan maka status transaksi menjadi EXPIRED dan TIDAK BISA DIGUNAKAN KEMBALI",
    );
    setPopupTextConfirm("Ya, Batalkan");
    setPopupCancel(true);
  };

  const toggleDetail = (idx: number) => {
    setOpenDetails((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
  };

  useEffect(() => {
    setAppBar({ title: "Detail Laporan VA", show: true, leading: "back" });
    setHeader({
      buttonBack: true,
      filter: false,
      title: "Detail Laporan VA",
      image: false,
    });
    setBottomNavigation("hidden");
  }, [setHeader, setAppBar, setBottomNavigation]);

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText, messageType, messageApi]);

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        <div className=" tw-rounded-lg card-body">
          <div className="tw-text-accent-primary tw-text-xs">Status</div>
          <div className="tw-mb-4">
            {status === "PENDING" ? (
              <span className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-yellow-50 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-yellow-700 tw-ring-1 tw-ring-yellow-600/10 tw-ring-inset">
                PENDING
              </span>
            ) : status === "EXPIRED" ? (
              <span className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-red-50 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-red-700 tw-ring-1 tw-ring-red-600/10 tw-ring-inset">
                EXPIRED
              </span>
            ) : (
              <span className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-green-50 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-green-700 tw-ring-1 tw-ring-green-600/10 tw-ring-inset">
                COMPLETE
              </span>
            )}
          </div>

          <div className="tw-text-accent-primary tw-text-xs">ID Transaksi</div>
          <div className="tw-mb-4 tw-text-text-primary tw-font-semibold">
            {state.vaTrxId ?? "-"}
          </div>

          <div className="tw-text-accent-primary tw-text-xs">Nomor VA</div>
          <div
            className="tw-mb-4 tw-text-text-primary tw-font-semibold tw-tracking-wide"
            onClick={onCopy}
          >
            {state?.vaNumber ?? "-"}&nbsp;
            <i>
              <IoCopy />
            </i>
          </div>

          <div className="tw-text-accent-primary tw-text-xs">Atas Nama</div>
          <div className="tw-mb-4 tw-text-text-primary tw-font-semibold">
            {state.vaName ?? "-"}
          </div>

          <div className="tw-text-accent-primary tw-text-xs">Nama Bank</div>
          <div className="tw-mb-4 tw-text-text-primary tw-font-semibold">
            {state.vaBankName ?? "-"}
          </div>

          <div className="tw-text-accent-primary tw-text-xs">Nominal</div>
          <div className="tw-mb-4 tw-text-text-primary tw-font-semibold">
            {toRp(
              state.vaBankName?.toLowerCase() === "bni"
                ? state?.vaTrxAmount
                : state?.vaTrxAmount - state?.vaBankFee,
            )}
          </div>

          <div className="tw-text-accent-primary tw-text-xs">
            Biaya Admin Bank
          </div>
          <div className="tw-mb-4 tw-text-text-primary tw-font-semibold">
            {toRp(state?.vaBankFee ?? 0)}
          </div>

          <div className="tw-text-accent-primary tw-text-xs">Total</div>
          <div
            className="tw-mb-4 tw-text-text-primary tw-font-bold tw-tracking-wide"
            onClick={onCopyNominal}
          >
            {state?.vaTrxAmount === 0
              ? toRp(state?.vaBankFee ?? 0)
              : toRp(
                  state.vaBankName?.toLowerCase() === "bni"
                    ? (state?.vaTrxAmount ?? 0) + (state?.vaBankFee ?? 0)
                    : (state?.vaTrxAmount ?? 0),
                )}
            &nbsp;
            <i>
              <IoCopy />
            </i>
          </div>

          <div className="tw-text-accent-primary tw-text-xs">
            Tanggal {status === "COMPLETED" ? "Bayar" : "Kadaluarsa"}
          </div>
          <div className="tw-mb-4 tw-text-text-primary tw-font-semibold">
            {status === "COMPLETED"
              ? moment(state.vaDatePayment ?? Date.now()).format(
                  "dddd, DD MMMM YYYY | HH:mm",
                )
              : moment(state.vaDateExpired ?? Date.now()).format(
                  "dddd, DD MMMM YYYY | HH:mm",
                )}
          </div>
        </div>

        <div className="tw-mt-4 tw-rounded-lg card-body">
          <div
            className="tw-text-text-primary tw-font-semibold tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
            onClick={() => toggleDetail(0)}
          >
            <span>
              Detail (
              {state.detail.tagihan?.length +
                (state.detail.uangsaku != "0" ? 1 : 0)}
              )
            </span>
            {openDetails.includes(0) ? <IoChevronUp /> : <IoChevronDown />}
          </div>

          {openDetails.includes(0) && (
            <>
              {state.detail.uangsaku != "0" && (
                <div className="tw-mt-3 tw-flex tw-justify-between tw-text-sm">
                  <div className="tw-text-accent-primary tw-w-1/2">
                    Uang Saku
                  </div>
                  <div className="tw-text-text-primary tw-font-semibold">
                    {toRp(
                      parseFloat(
                        (state.detail.uangsaku ?? "0").replace(",", ""),
                      ),
                    )}
                  </div>
                </div>
              )}

              {state.detail.tagihan?.length > 0 &&
                state.detail.tagihan?.map(
                  (item: DetailRequestTagihanVA, idx: number) => (
                    <div
                      key={idx}
                      className="tw-mt-3 tw-flex tw-justify-between tw-text-sm"
                    >
                      <div className="tw-text-accent-primary tw-w-1/2">
                        {item.tagihan}
                      </div>
                      <div className="tw-text-text-primary tw-font-semibold">
                        {toRp(
                          parseFloat((item.dibayar ?? "0").replace(",", "")),
                        )}
                      </div>
                    </div>
                  ),
                )}
            </>
          )}
        </div>
        {status === "PENDING" && (
          <Button
            type="primary"
            loading={isPending}
            className="tw-p-2 tw-mt-4 tw-h-10 tw-w-full tw-text-base tw-rounded-xl bg-gradient tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
            onClick={() => onOpenPopupCancel(state?.vaTrxId ?? "")}
          >
            Batalkan Pembayaran
          </Button>
        )}
      </div>

      <PopupConfirm
        open={popupCancel}
        title={popupTitle}
        message={popupMessage}
        textConfirm={popupTextConfirm}
        textCancel="Kembali"
        onConfirm={() => handleConfirm()}
        onCancel={() => setPopupCancel(false)}
      />
    </>
  );
};
